/**
 * Section B, the client's voice.
 *
 * ONE command, and it writes the most consequential column in the Brand Bible.
 * `voice_summary` is a `context` column, so `projection.py:284` projects it into
 * every generation prompt the moment it lands and nothing verifies it on the way
 * through. `writes-config` would be a lie about that, which is why the
 * consequence is `writes-generation-input` and the diff carries
 * `reachesGeneratedContent` rather than leaving the operator to infer it.
 *
 * THE CREATE PATH IS OPEN, AND THE READ SEAM IS WHAT KEEPS IT HONEST.
 * `capabilities.currentRow` resolves a singleton by table now
 * (`capabilities.ts:94`), so a null read is no longer a blind seam. It is two
 * different facts wearing one value. Either this tenant has no voice profile
 * yet, which is the legitimate create the writer already handles, or the Brand
 * Bible never settled for this tenant, and creating a row on that basis is a
 * write over server state this command never read. `read.brandBibleLoaded`
 * answers that directly, being `awaitLoaded` on the very store `currentRow`
 * reads. Loaded true beside a null row is an absent row, so the create stands.
 * Loaded false is the refusal.
 *
 * The seam replaced a proxy, and the proxy is why an empty client used to be
 * refused. Settlement was inferred from a sibling singleton resolving, which
 * proves a load happened but says nothing about a tenant whose Brand Bible
 * loaded holding nothing at all. That tenant now creates. A capability set
 * built before the seam existed carries no `brandBibleLoaded`, so the sibling
 * probe survives as the fallback for those and only for those.
 */
import { z } from 'zod';
import {
  defineCommand,
  isRefusal,
  patchFromDiff,
  refuse,
  renderPatchValue,
  type CommitAuthorization,
  type Diff,
  type DiffChange,
  type Receipt,
  type Refusal,
  type TenantId,
  type WriteDeps,
} from '../contract';

/** `WRITABLE_TABLES` name, not the `bb_` prefixed physical table. */
const VOICE_TABLE = 'voice_profile';

/**
 * The sibling singleton read as a proxy for settlement, and ONLY when the
 * capability set predates `read.brandBibleLoaded`.
 *
 * Section A, so it is the row most likely to already exist when Section B does
 * not, and a singleton, so it resolves through the same id-less path rather
 * than through a rule this command would have to restate. Never consulted when
 * the seam is present, because the seam answers the question this only hints
 * at.
 */
const IDENTITY_TABLE = 'identity';

/**
 * Module scope, because a schema rebuilt per call is a new object identity on
 * every store tick and the surface re-registers on identity.
 */
const setVoiceSummaryArgs = z.object({
  voice_summary: z
    .string()
    .min(40)
    .max(1200)
    .describe(
      'The client voice summary, 40 to 1200 characters. Plain guidance on tone, person and reading level. This exact text is injected into every article prompt for this client.',
    ),
  attributes: z
    .array(z.string().min(1).max(39))
    .min(3)
    .max(7)
    .optional()
    .describe(
      '3 to 7 short voice attributes, each under 40 characters, for example "plain-spoken" or "never hypes the brand".',
    ),
});

/**
 * `summaryFrom` and `attributesFrom` are rendered here rather than in `diff`
 * so the target carries the whole before-state and `diff` stays pure over its
 * own argument.
 */
interface VoiceSummaryTarget {
  readonly rowId: string | null;
  readonly summary: string;
  readonly summaryFrom: string | null;
  /** Null when the caller left attributes alone, which is not the same as clearing them. */
  readonly attributes: readonly string[] | null;
  readonly attributesFrom: string | null;
}

/**
 * A stored value as the confirmation should read it. Absent and null both
 * render as no prior value, because the column being unset and the column
 * holding SQL NULL are the same fact to the operator approving the change.
 */
function renderFrom(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  return renderPatchValue(value);
}

/**
 * The target for a row, or for the absence of one.
 *
 * A null row is the create. `rowId` null is what the writer's create path takes
 * and what the runtime maps back to an id-less base-version read, and every
 * `from` renders as no prior value because there is no prior row to have held
 * one.
 */
function targetFrom(
  row: Readonly<Record<string, unknown>> | null,
  args: z.infer<typeof setVoiceSummaryArgs>,
): VoiceSummaryTarget {
  const id = row?.['id'];
  return {
    rowId: typeof id === 'string' && id.length > 0 ? id : null,
    summary: args.voice_summary,
    summaryFrom: renderFrom(row?.['voice_summary']),
    attributes: args.attributes ?? null,
    attributesFrom: renderFrom(row?.['attributes']),
  };
}

/**
 * The one description of this write.
 *
 * `diff` and `run` both go through it, so the patch the writer receives is
 * `patchFromDiff` of the exact `Diff` the runtime digested. Building the patch
 * a second way is how a command mints an authorization over one shape and sends
 * another, and the writer then refuses every write with a digest mismatch.
 */
function voiceDiff(target: VoiceSummaryTarget, tenant: TenantId): Diff {
  const changes: DiffChange[] = [
    {
      field: 'voice_summary',
      from: target.summaryFrom,
      to: renderPatchValue(target.summary),
      authorability: 'prose',
    },
  ];

  if (target.attributes !== null) {
    changes.push({
      field: 'attributes',
      from: target.attributesFrom,
      to: renderPatchValue(target.attributes),
      // Required. An array is not a string, so the digest must bind the typed
      // value and not its rendering.
      value: target.attributes,
      authorability: 'prose',
    });
  }

  return {
    table: VOICE_TABLE,
    rowId: target.rowId,
    tenant,
    changes,
    reachesGeneratedContent: true,
  };
}

export const bbSetVoiceSummary = defineCommand<
  z.infer<typeof setVoiceSummaryArgs>,
  VoiceSummaryTarget
>({
  name: 'bb_set_voice_summary',
  section: 'B',
  consequence: 'writes-generation-input',
  description:
    'Set the client voice summary in Section B of the Brand Bible, and optionally 3 to 7 short voice attributes. This text is injected into the prompt of EVERY article generated for this client, with no verification step in between, so it changes all future writing for them straight away. Replaces the whole summary rather than appending to it.',
  args: setVoiceSummaryArgs,

  async resolve(input) {
    const { args, read, signal, tenant } = input;

    const row = await read.currentRow(VOICE_TABLE, tenant);
    if (row !== null) return targetFrom(row, args);

    // A second bounded store wait on a call the caller has already abandoned
    // buys nothing, and `awaitLoaded` is bounded in tens of seconds.
    if (signal.aborted) {
      return refuse('The call was cancelled before the Brand Bible settled. Nothing was written.', {
        retryable: true,
      });
    }

    const settled =
      (await read.brandBibleLoaded?.(tenant)) ??
      ((await read.currentRow(IDENTITY_TABLE, tenant)) !== null);

    if (!settled) {
      return refuse(
        `The Brand Bible has not loaded for ${tenant}, so this tool cannot tell a client who has no ` +
          'voice profile yet from one whose Brand Bible it never read, and it will not create a row ' +
          "over server state it has not seen. Open that client's Brand Bible, then ask again.",
        { retryable: true },
      );
    }

    // Settlement was established AFTER the read above, by a seam that waits for
    // it, so the read is taken again rather than trusted. A stale null would
    // mint a create against a row that already exists, which the writer refuses
    // as a malformed authorization and the operator cannot act on.
    return targetFrom(await read.currentRow(VOICE_TABLE, tenant), args);
  },

  diff(target, tenant) {
    return voiceDiff(target, tenant);
  },

  async run(
    target: VoiceSummaryTarget,
    auth: CommitAuthorization,
    write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    const receipt = await write.commit(auth, patchFromDiff(voiceDiff(target, auth.tenant)));
    if (isRefusal(receipt)) return receipt;

    // The writer reports the row it wrote and cannot know what the column
    // means, so the blast radius is said here or it is said nowhere. Worded to
    // hold whether the write landed or the values already matched, because in
    // both cases the summary is what the next article is generated from.
    return {
      ...receipt,
      summary: `${receipt.summary} This voice summary is injected into every article generated for ${auth.tenant}, with no verification gate in between.`,
    };
  },
});
